import { useApi } from '@classgo/app-kit';
import type { AlbertLessonPlanView } from '@classgo/shared';
import { Button, EmptyState, Field, Input, useToast } from '@classgo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { albertApi } from '../../albert/api';
import { useT } from '../../i18n';
import { RefusalNotice } from './refusal-notice';
import { formatDateTime } from './support';
import type { PlanPanelProps } from './types';

/**
 * The saved plan as the teacher edits it: title and Markdown content in a textarea, "Guardar
 * cambios" enabled only when something changed and the API's minimums hold. Keyed by the plan's
 * version from the panel, so a regenerated or reloaded plan starts a fresh editor.
 */
const PlanEditor = ({
  plan,
  onSaved,
}: {
  plan: AlbertLessonPlanView;
  onSaved: () => Promise<unknown>;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const contentId = useId();
  const [title, setTitle] = useState(plan.title);
  const [content, setContent] = useState(plan.content);
  const save = useMutation({
    mutationFn: () =>
      albertApi(api).savePlan(plan.id, { title: title.trim(), content: content.trim() }),
    onSuccess: async () => {
      toast.show({ tone: 'success', title: t('albert.plan.savedToast') });
      await onSaved();
    },
  });
  const dirty = title !== plan.title || content !== plan.content;
  return (
    <form
      className="space-y-3"
      aria-label={t('albert.plan.editor')}
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty) save.mutate();
      }}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>{t('albert.plan.updatedAt', { date: formatDateTime(plan.updatedAt) })}</span>
        {dirty ? (
          <span className="rounded-full bg-warning-100 px-2 py-0.5 text-warning-700">
            {t('albert.plan.unsaved')}
          </span>
        ) : null}
      </div>
      <Input
        label={t('albert.plan.title')}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={200}
        required
      />
      <Field id={contentId} label={t('albert.plan.content')}>
        <textarea
          id={contentId}
          className="w-full rounded-control border border-gray-300 p-3 text-sm leading-6 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          rows={22}
          value={content}
          onChange={(event) => setContent(event.target.value)}
          required
          minLength={20}
        />
      </Field>
      {save.isError ? (
        <RefusalNotice error={save.error} retrying={save.isPending} onRetry={() => save.mutate()} />
      ) : null}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="secondary"
          loading={save.isPending}
          disabled={!dirty || title.trim().length < 3 || content.trim().length < 20}
        >
          {t('albert.plan.save')}
        </Button>
      </div>
    </form>
  );
};

/**
 * The lesson plan of the topic: generate it (with an optional focus in the teacher's words),
 * then read and edit it. Generating again replaces the saved plan; the topic's list query is
 * refreshed after every write so the screen always shows what the server holds.
 */
export const PlanPanel = ({ pair, topic, plan, loading, onQuota }: PlanPanelProps) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const [focus, setFocus] = useState('');
  const invalidate = () =>
    queries.invalidateQueries({ queryKey: ['albert', 'lesson-plans', topic.id] });
  const generate = useMutation({
    mutationFn: () =>
      albertApi(api).generatePlan({
        topicId: topic.id,
        groupId: pair.groupId,
        ...(focus.trim() ? { focus: focus.trim() } : {}),
      }),
    onSuccess: async (result) => {
      onQuota(result.quota);
      toast.show({ tone: 'success', title: t('albert.plan.generatedToast') });
      await invalidate();
    },
  });
  const working = generate.isPending;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start">
        <Input
          label={t('albert.plan.focus')}
          help={t('albert.plan.focus.help')}
          value={focus}
          onChange={(event) => setFocus(event.target.value)}
          maxLength={300}
          disabled={working}
        />
        <Button className="sm:mt-6" loading={working} onClick={() => generate.mutate()}>
          {plan ? t('albert.plan.regenerate') : t('albert.plan.generate')}
        </Button>
      </div>
      {plan && !working ? (
        <p className="text-xs text-gray-500">{t('albert.plan.regenerate.help')}</p>
      ) : null}
      {working ? (
        <p role="status" className="text-sm text-gray-600">
          {t('albert.plan.working')}
        </p>
      ) : null}
      {generate.isError && !working ? (
        <RefusalNotice
          error={generate.error}
          retrying={working}
          onRetry={() => generate.mutate()}
        />
      ) : null}
      {plan ? (
        <PlanEditor key={`${plan.id}:${plan.updatedAt}`} plan={plan} onSaved={invalidate} />
      ) : loading || working ? null : (
        <EmptyState
          title={t('albert.plan.empty.title')}
          description={t('albert.plan.empty.body')}
        />
      )}
    </div>
  );
};
